/**
 * ターン開始時スナップショット ユーティリティ
 *
 * Phase 1 開始時にゲーム状態を保存し、Settings Modal から復元可能にする。
 * Phase 9 (refillGoods) 以降は Undo 不可。
 */

import type { AoSGameState } from '../../types';

/**
 * Undo 不可フェーズのリスト
 * これらのフェーズでは「ターン開始に戻る」ボタンを無効化
 * - issueShares: ターン開始直後なのでリセット不要
 * - refillGoods, advanceTurn: ターン終了処理のため不可逆
 */
export const UNDO_BLOCKED_PHASES = ['issueShares', 'refillGoods', 'advanceTurn'] as const;

/**
 * 指定フェーズで Undo 可能かどうかを判定
 */
export function canUndoToTurnStart(
  phase: string | null | undefined,
  hasSnapshot: boolean
): boolean {
  if (!hasSnapshot) return false;
  if (!phase) return false;
  return !UNDO_BLOCKED_PHASES.includes(phase as typeof UNDO_BLOCKED_PHASES[number]);
}

/**
 * スナップショット保存時に除外するフィールド
 * これらは一時的な状態であり、復元時には不要
 */
const EXCLUDED_FIELDS = [
  'turnStartSnapshot',        // 再帰防止
  'forcePhaseToIssueShares',  // Undo 用一時フラグ（復元時に再設定される）
  'autoPhaseResult',          // 一時的な表示状態
  'buildTrackPhaseSnapshot',  // Phase 4 用の別スナップショット
  'productionSnapshot',       // Phase 9 用の別スナップショット
  'buildCostInfo',            // UI 表示用の一時計算結果
  'undoCount',                // Undo 回数は累積（復元で上書きしない）
] as const;

/**
 * ターン開始時のスナップショットを作成
 *
 * @param G - 現在のゲーム状態
 * @returns JSON 文字列としてシリアライズされたスナップショット
 */
export function createTurnStartSnapshot(G: AoSGameState): string {
  // 除外フィールドを取り除いてコピー
  const snapshot: Partial<AoSGameState> = {};

  for (const key of Object.keys(G) as (keyof AoSGameState)[]) {
    if (!EXCLUDED_FIELDS.includes(key as typeof EXCLUDED_FIELDS[number])) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (snapshot as any)[key] = G[key];
    }
  }

  return JSON.stringify(snapshot);
}

/**
 * スナップショットからゲーム状態を復元
 *
 * @param G - 復元先のゲーム状態（ミューテーション対象）
 * @returns 復元成功なら true、失敗なら false
 */
export function restoreFromTurnStartSnapshot(G: AoSGameState): boolean {
  if (!G.turnStartSnapshot) {
    return false;
  }

  try {
    const snapshot = JSON.parse(G.turnStartSnapshot) as Partial<AoSGameState>;

    // スナップショットの各フィールドを G に復元
    for (const key of Object.keys(snapshot) as (keyof AoSGameState)[]) {
      // 除外フィールドは復元しない（念のため）
      if (EXCLUDED_FIELDS.includes(key as typeof EXCLUDED_FIELDS[number])) {
        continue;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (G as any)[key] = JSON.parse(JSON.stringify(snapshot[key]));
    }

    return true;
  } catch {
    console.error('[restoreFromTurnStartSnapshot] Failed to parse snapshot');
    return false;
  }
}
